// Drives a carousel footer (page counter + prev/next) by talking to the
// nearest <slideshow-component> in its section via the public next()/previous()
// methods and the bubbling 'slideshow:select' event.
//
// Registered from an asset module (not an inline {% javascript %} block) so the
// element is defined on the initial page load even for sections that render the
// carousel-nav ASYNCHRONOUSLY (e.g. the PDP product-recommendations carousel,
// which is injected via morphSection after its skeleton). An inline snippet
// {% javascript %} block is only bundled when the snippet renders on the first
// paint, so it never ran for the morph-hydrated recommendations section.
class CarouselNav extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.slideshow = this.section && this.section.querySelector('slideshow-component');
    this.prevButton = this.querySelector('[data-direction="previous"]');
    this.nextButton = this.querySelector('[data-direction="next"]');
    this.counter = this.querySelector('.carousel-nav__counter');
    this.columns = parseInt(this.dataset.columns, 10) || 1;
    this.columnsMobile = parseInt(this.dataset.columnsMobile, 10) || this.columns;
    this.total = parseInt(this.dataset.total, 10) || 0;

    if (!this.slideshow) {
      this.hidden = true;
      return;
    }

    this.handlePrev = () => this.slideshow.previous();
    this.handleNext = () => this.slideshow.next();
    // A card may contain its own nested <slideshow-component> (e.g. a product
    // image gallery) that emits the same bubbling event. Only react to the
    // outer carousel or the counter/arrows desync.
    this.handleSelect = (event) => {
      if (event.target !== this.slideshow) return;
      this.update();
    };
    this.handleResize = () => this.update();

    this.prevButton.addEventListener('click', this.handlePrev);
    this.nextButton.addEventListener('click', this.handleNext);
    this.section.addEventListener('slideshow:select', this.handleSelect);
    window.addEventListener('resize', this.handleResize);

    // Recompute until the slideshow's IntersectionObserver has populated
    // visibleSlides. It is empty on first paint, and on morph-hydrated sections
    // (e.g. the PDP recommendations carousel) it settles several frames later
    // without emitting a slideshow:select — so a one-shot rAF would miss it.
    this.update();
    let settleAttempts = 0;
    const settle = () => {
      if (!this.isConnected) return;
      this.update();
      const settled = this.slideshow.visibleSlides && this.slideshow.visibleSlides.length > 0;
      if (!settled && settleAttempts++ < 30) {
        this.settleRaf = requestAnimationFrame(settle);
      }
    };
    this.settleRaf = requestAnimationFrame(settle);
  }

  disconnectedCallback() {
    if (this.settleRaf) cancelAnimationFrame(this.settleRaf);
    this.prevButton && this.prevButton.removeEventListener('click', this.handlePrev);
    this.nextButton && this.nextButton.removeEventListener('click', this.handleNext);
    this.section && this.section.removeEventListener('slideshow:select', this.handleSelect);
    window.removeEventListener('resize', this.handleResize);
  }

  // Slides visible per page: the slideshow's real count once observed, else the
  // per-breakpoint columns fallback.
  #visiblePerPage() {
    const visibleNow = this.slideshow.visibleSlides ? this.slideshow.visibleSlides.length : 0;
    if (visibleNow > 0) return visibleNow;
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    return isDesktop ? this.columns : this.columnsMobile;
  }

  update() {
    // Total = the real number of rendered slides (ignores disabled blocks),
    // falling back to the liquid-provided total.
    const refSlides = this.slideshow.refs && this.slideshow.refs.slides;
    const total = (refSlides && refSlides.length) || this.total;
    const index = this.slideshow.current || 0;
    const visible = this.#visiblePerPage();
    const totalPages = Math.max(1, Math.ceil(total / visible));
    const page = Math.min(totalPages, Math.floor(index / visible) + 1);
    const pad = (value) => String(value).padStart(2, '0');

    if (this.counter) this.counter.textContent = `${pad(page)} — ${pad(totalPages)}`;
    if (this.prevButton) this.prevButton.disabled = index <= 0;
    if (this.nextButton) this.nextButton.disabled = index + visible >= total;
  }
}

if (!customElements.get('carousel-nav')) {
  customElements.define('carousel-nav', CarouselNav);
}
